/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */

import type {File as AttachmentFile} from '@/core/util/validation/rules';

const DEFAULT_MAX_SIZE = 2 * 1024 * 1024;
const MAX_WIDTH = 1024;
const MAX_HEIGHT = 768;
const MAX_ENCODE_ATTEMPTS = 8;
const OUTPUT_TYPE = 'image/jpeg';

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = src;
  });

const canvasToBlob = (
  canvas: HTMLCanvasElement,
  type: string,
  quality?: number,
): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob ? resolve(blob) : reject(new Error('Failed to compress image')),
      type,
      quality,
    );
  });

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1]);
    };
    reader.onerror = () => reject(new Error('Failed to read compressed image'));
    reader.readAsDataURL(blob);
  });

/** Scale down to fit within max width/height while preserving aspect ratio. */
const scaleDimensions = (
  width: number,
  height: number,
  maxWidth: number,
  maxHeight: number,
): {width: number; height: number} => {
  if (width <= maxWidth && height <= maxHeight) {
    return {width, height};
  }
  const ratio = Math.min(maxWidth / width, maxHeight / height);
  return {
    width: Math.round(width * ratio),
    height: Math.round(height * ratio),
  };
};

const encodeCanvas = async (
  canvas: HTMLCanvasElement,
  maxSizeBytes: number,
): Promise<Blob | null> => {
  for (let quality = 0.85; quality >= 0.5; quality -= 0.1) {
    const blob = await canvasToBlob(canvas, OUTPUT_TYPE, quality);
    if (blob.size <= maxSizeBytes) {
      return blob;
    }
  }

  const blob = await canvasToBlob(canvas, OUTPUT_TYPE, 0.5);
  return blob.size <= maxSizeBytes ? blob : null;
};

const toProcessedFile = async (
  file: AttachmentFile,
  blob: Blob,
): Promise<AttachmentFile> => {
  const base64 = await blobToBase64(blob);
  const name = file.name.replace(/\.[^.]+$/i, '') + '.jpg';
  return {
    name,
    type: OUTPUT_TYPE,
    size: blob.size,
    base64,
  };
};

/**
 * Resize and/or compress an attachment image. Dimensions are capped at 1024×768
 * (aspect ratio preserved). JPEG quality is reduced when needed to stay under
 * maxSizeBytes. Returns the original file when already within both limits.
 */
export const compressImageFile = async (
  file: AttachmentFile,
  maxSizeBytes = DEFAULT_MAX_SIZE,
): Promise<AttachmentFile | null> => {
  if (!file?.base64) {
    return null;
  }

  const img = await loadImage(`data:${file.type};base64,${file.base64}`);
  const needsResize =
    img.naturalWidth > MAX_WIDTH || img.naturalHeight > MAX_HEIGHT;
  const needsCompress = file.size > maxSizeBytes;

  if (!needsResize && !needsCompress) {
    return file;
  }

  const aspectRatio = img.naturalWidth / img.naturalHeight;
  let {width, height} = scaleDimensions(
    img.naturalWidth,
    img.naturalHeight,
    MAX_WIDTH,
    MAX_HEIGHT,
  );

  for (let attempt = 0; attempt < MAX_ENCODE_ATTEMPTS; attempt++) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return null;
    }
    ctx.drawImage(img, 0, 0, width, height);

    const blob = await encodeCanvas(canvas, maxSizeBytes);
    if (blob) {
      return toProcessedFile(file, blob);
    }

    width = Math.max(1, Math.round(width * 0.8));
    height = Math.max(1, Math.round(width / aspectRatio));
  }

  return null;
};
